/**
 * The small amount of formatting a comment may carry, and the safe way to
 * render it.
 *
 * THE RULE THIS FILE EXISTS TO KEEP: comment text is never parsed as markup.
 *
 * Adding bold and italic is exactly the change that tempts someone to store
 * HTML and call innerHTML, which would hand every commenter a script tag on a
 * page other people read. Instead a comment stays plain text in the database,
 * written in a four-token subset of Markdown, and this renderer turns that
 * into a DocumentFragment built with createElement and textContent.
 *
 * There is no HTML parser anywhere on the path, so there is nothing for a
 * `<script>`, an `onerror=`, a `javascript:` URL or a stray `<` to attach to:
 * every character a commenter types that is not one of the four markers ends
 * up inside a text node, where markup has no meaning.
 *
 *   **bold**      ->  <strong>
 *   _italic_      ->  <em>
 *   > quoted      ->  <blockquote>, consecutive lines merged
 *   blank line    ->  paragraph break
 *
 * Links are deliberately absent. A link is the token whose whole purpose is
 * to point somewhere, which makes it the one that needs a URL scheme
 * allowlist, a rel=noopener policy and a spam answer. Bare URLs in a comment
 * stay text.
 */
(() => {
  // `[^*]+` and `[^_]+` rather than lazy `.+?`: a bounded character class
  // cannot backtrack catastrophically on a long line of markers, which a
  // commenter is perfectly able to paste.
  const INLINE = /(\*\*[^*]+\*\*|_[^_]+_)/g;

  // Anchored forms of the same patterns. A piece is treated as a marker only
  // if it MATCHES one of these -- not if it merely starts and ends with the
  // right characters.
  //
  // That distinction is load-bearing. The first version checked
  // `startsWith("_") && endsWith("_")`, which is true of a run of plain
  // underscores as well: 4000 of them recursed into 2000 nested <em>
  // elements, each pass shaving one character off each end. Measured, not
  // theorised. `____` does not match /^_([^_]+)_$/, so it is text.
  const BOLD = /^\*\*([^*]+)\*\*$/;
  const ITALIC = /^_([^_]+)_$/;

  // Belt and braces on top of the anchored patterns: markers do not nest
  // meaningfully past a couple of levels, and a bound means no input can
  // drive the DOM deeper than this however the patterns are edited later.
  const MAX_DEPTH = 6;

  /** Inline markers inside one line, appended to `target` as real nodes. */
  function inline(target, line, depth = 0) {
    for (const piece of line.split(INLINE)) {
      if (!piece) continue;

      const bold = depth < MAX_DEPTH && BOLD.exec(piece);
      if (bold) {
        const strong = document.createElement("strong");
        // Recurse, so `**_both_**` is bold containing italic rather than bold
        // text with literal underscores in it.
        inline(strong, bold[1], depth + 1);
        target.append(strong);
        continue;
      }

      const italic = depth < MAX_DEPTH && ITALIC.exec(piece);
      if (italic) {
        const em = document.createElement("em");
        inline(em, italic[1], depth + 1);
        target.append(em);
        continue;
      }

      // Everything else, including any < > & " ' the person typed, becomes
      // a text node. This is the line that makes the whole file safe.
      target.append(document.createTextNode(piece));
    }
  }

  /** Text -> DocumentFragment. Never returns a string, so it cannot be assigned to innerHTML by accident. */
  function render(text) {
    const fragment = document.createDocumentFragment();
    const lines = String(text ?? "").replace(/\r\n/g, "\n").split("\n");

    let i = 0;
    while (i < lines.length) {
      const line = lines[i];

      if (/^\s*>\s?/.test(line)) {
        const quote = document.createElement("blockquote");
        // Consecutive quoted lines are one blockquote, not one each.
        while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
          if (quote.childNodes.length) quote.append(document.createElement("br"));
          inline(quote, lines[i].replace(/^\s*>\s?/, ""));
          i++;
        }
        fragment.append(quote);
        continue;
      }

      if (line.trim() === "") { i++; continue; }

      const p = document.createElement("p");
      // A run of ordinary lines is one paragraph with breaks, which is what
      // someone pressing Enter once actually meant.
      while (i < lines.length && lines[i].trim() !== "" && !/^\s*>\s?/.test(lines[i])) {
        if (p.childNodes.length) p.append(document.createElement("br"));
        inline(p, lines[i]);
        i++;
      }
      fragment.append(p);
    }

    return fragment;
  }

  /* ------------------------------------------------------------ editing

     The composer is a contenteditable element, not a textarea, because the
     ask was to SEE bold text rather than to see `**bold**`. A textarea cannot
     render its own contents.

     That changes what has to be defended. A contenteditable will happily
     accept pasted HTML from anywhere, so two things stand between it and the
     database:

       1. Paste is intercepted and reinserted as plain text, so foreign markup
          never enters the element at all.
       2. Whatever IS in the element is serialised by walking the DOM, and the
          walker understands exactly six tags. Anything else contributes its
          text and nothing more.

     So the value that leaves the browser is the same plain-text subset as
     before, the database still holds plain text, and render() above is still
     the only thing that turns it back into elements. The editor is a nicer
     way to type the markers -- it is not a new storage format.
  */

  const BLOCK = new Set(["DIV", "P", "BLOCKQUOTE", "LI", "TR"]);

  /* Elements whose text is not content. Walking into a pasted <script> would
     put its source code in the comment as literal text -- safe, because it is
     only ever text from here on, but nonsense to read. Dropped whole. */
  const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEMPLATE", "IFRAME", "OBJECT", "SVG"]);

  /** contenteditable DOM -> the plain-text subset. */
  function serialize(root) {
    const out = [];

    const walk = (node, inQuote) => {
      for (const child of node.childNodes) {
        if (child.nodeType === 3) {
          out.push(child.textContent);
          continue;
        }
        if (child.nodeType !== 1) continue;

        const tag = child.tagName;

        if (SKIP.has(tag)) continue;
        if (tag === "BR") { out.push("\n"); continue; }

        if (tag === "BLOCKQUOTE") {
          if (out.length && !/\n$/.test(out.join(""))) out.push("\n");
          const mark = out.length;
          walk(child, true);
          // Prefix every line the quote produced, once it is known.
          const body = out.splice(mark).join("");
          out.push(body.split("\n").map((l) => `> ${l}`).join("\n"));
          out.push("\n");
          continue;
        }

        // Bold and italic RECURSE rather than taking textContent. Reading
        // textContent flattens whatever is nested inside, which is exactly
        // how `<b><i>both</i></b>` -- what the editor correctly produces when
        // both tools are on -- came back out as `**both**` with the italic
        // silently dropped.
        if (tag === "STRONG" || tag === "B") {
          // An empty or whitespace-only pair would render as literal
          // asterisks, so skip it rather than emit `****`.
          if (!child.textContent.trim()) continue;
          out.push("**");
          walk(child, inQuote);
          out.push("**");
          continue;
        }

        if (tag === "EM" || tag === "I") {
          if (!child.textContent.trim()) continue;
          out.push("_");
          walk(child, inQuote);
          out.push("_");
          continue;
        }

        if (BLOCK.has(tag)) {
          if (out.length && !/\n$/.test(out.join(""))) out.push("\n");
          walk(child, inQuote);
          out.push("\n");
          continue;
        }

        // Any other element -- a pasted <span>, a stray <font>, anything a
        // browser invented -- contributes its children and no markup.
        walk(child, inQuote);
      }
    };

    walk(root, false);

    return out
      .join("")
      .replace(/\u00a0/g, " ")      // contenteditable leaves nbsp everywhere
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  /**
   * Apply a format to the current selection inside a contenteditable.
   *
   * execCommand is deprecated and still the only thing every browser
   * implements for selection-aware bold and italic. The alternative is a
   * range-splitting editor of a few hundred lines, for a comment box.
   */
  function toggle(field, kind) {
    field.focus();
    try {
      if (kind === "bold") document.execCommand("bold");
      else if (kind === "italic") document.execCommand("italic");
      else if (kind === "quote") {
        const inQuote = Boolean(closestTag(field, "BLOCKQUOTE"));
        document.execCommand("formatBlock", false, inQuote ? "p" : "blockquote");
      }
    } catch {
      /* A refused command leaves the text as it was, which is survivable. */
    }
    field.dispatchEvent(new Event("input", { bubbles: true }));
  }

  /** Whether the caret sits inside `tag`, bounded by the editor itself. */
  function closestTag(field, tag) {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return null;
    let node = selection.getRangeAt(0).startContainer;
    while (node && node !== field) {
      if (node.nodeType === 1 && node.tagName === tag) return node;
      node = node.parentNode;
    }
    return null;
  }

  /** Which formats are on at the caret, for the toolbar's pressed state. */
  function activeFormats(field) {
    const state = { bold: false, italic: false, quote: false };
    try {
      state.bold = document.queryCommandState("bold");
      state.italic = document.queryCommandState("italic");
    } catch { /* queryCommandState throws in some embedded views */ }
    state.quote = Boolean(closestTag(field, "BLOCKQUOTE"));
    return state;
  }

  /**
   * Paste as plain text. This is the gate that keeps foreign markup out of
   * the editor in the first place; the serializer is the second line, not
   * the only one.
   */
  function handlePaste(event) {
    event.preventDefault();
    const text = (event.clipboardData || window.clipboardData)?.getData("text/plain") ?? "";
    try {
      document.execCommand("insertText", false, text);
    } catch {
      const selection = window.getSelection();
      if (!selection?.rangeCount) return;
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      selection.collapseToEnd();
    }
  }

  window.ESRichText = { render, serialize, toggle, activeFormats, handlePaste };

})();
